import { useEffect, useMemo, useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  Select, 
  MenuItem, 
  Switch, 
  FormControlLabel,
  CircularProgress,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { FiberManualRecord as StatusDotIcon } from '@mui/icons-material';
import { 
  Refresh as RefreshIcon,
  Visibility,
  VisibilityOff,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import PageLayout from '../../components/layout/frames/PageLayout';
import { refreshCameras, refreshChamber, getRaspiLogs, restartRaspiSoftware, rebootRaspi} from '../../services/api';
import { useRaspiStore } from '../../store/raspiStore';
import { useLogsStore } from '../../store/logsStore';
import { usePreferencesStore } from '../../store/preferencesStore';
import { useChamberStore } from '../../store/chamberStore';
import { useCameraStore } from '../../store/cameraStore';
import { useExperimentStore } from '../../store/experimentStore';
import type { RaspiDevice } from '../../types/raspi';
import type { Experiment } from '../../types/experiment';
import type { LogEntry } from '../../types/logs';
import ChamberCard from '../../components/cards/ChamberCard';
import CameraCard from '../../components/cards/CameraCard';
import ExperimentCard from '../../components/cards/ExperimentCard';
import ExperimentEditDialog from '../../components/dialogs/ExperimentEditDialog';
import RefreshConfirmationDialog from '../../components/dialogs/RefreshConfirmationDialog';
import LoadRaspiDataDialog from '../../components/dialogs/LoadRaspiDataDialog';
import CleanRaspiFilesDialog from '../../components/dialogs/CleanRaspiFilesDialog';
import UpdateRaspiSoftwareDialog from '../../components/dialogs/UpdateRaspiSoftwareDialog';
import { useAuthStore } from '../../store/authStore';
import { useUnauthorizedAction } from '../../App';
import { useLocation } from 'react-router-dom';
import React from 'react';
import RaspiStatusIndicator from '../../components/raspi/RaspiStatusIndicator';
import RaspiMenu from '../../components/raspi/RaspiMenu';
import { websocketService } from '../../services/websocket';
import { useTheme } from '@mui/material/styles';

const RaspiDetails = () => {
  const theme = useTheme();
  const getRole = useAuthStore((state) => state.getRole);
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();
  const { 
    allRaspis: raspis, 
    selectedRaspiId, 
    fetchAllRaspis, 
    selectRaspi 
  } = useRaspiStore();
  const { 
    raspiLogs: logEntries, 
    isLoading: logLoading, 
    setSelectedRaspiId,
    addLogEntries,
    clearLogs,
  } = useLogsStore();
  const { 
    showDisconnectedDevices, 
    showRaspiLog: showLogFrame, 
    logFrameExpanded: isLogFrameExpanded,
    updatePreference 
  } = usePreferencesStore();
  const {
    fetchAllChambers
  } = useChamberStore();
  const {
    fetchAllCameras,
    allCameras
  } = useCameraStore();
  const {
    fetchAllExperiments,
    allExperiments
  } = useExperimentStore();
  
  // Create stable references for fetch functions to prevent infinite re-renders
  const fetchAllRaspisRef = useRef(fetchAllRaspis);
  const fetchAllChambersRef = useRef(fetchAllChambers);
  const fetchAllCamerasRef = useRef(fetchAllCameras);
  const fetchAllExperimentsRef = useRef(fetchAllExperiments);
  
  
  // Experiment dialog state
  const [experimentDialogOpen, setExperimentDialogOpen] = useState(false);
  const [experimentDialogExpId, setExperimentDialogExpId] = useState<string | null>(null);
  
  // Refresh confirmation dialog states
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [refreshDialogType, setRefreshDialogType] = useState<'chamber' | 'cameras'>('chamber');
  const [refreshDialogRaspiId, setRefreshDialogRaspiId] = useState<string>('');
  
  // File transfer dialog states
  const [loadRaspiDataDialogOpen, setLoadRaspiDataDialogOpen] = useState(false);
  const [cleanRaspiFilesDialogOpen, setCleanRaspiFilesDialogOpen] = useState(false);
  const [updateRaspiSoftwareDialogOpen, setUpdateRaspiSoftwareDialogOpen] = useState(false);

  // Add at the top with other useState hooks
  const [showRebootWarning, setShowRebootWarning] = useState(false);
  const [showRestartWarning, setShowRestartWarning] = useState(false);

  // Helper functions
  const getSelectedRaspi = (): RaspiDevice | undefined => {
    const selectedRaspi = selectedRaspiId ? raspis[selectedRaspiId] : undefined;
    return selectedRaspi;
  };

  const getFilteredRaspis = (): Record<string, RaspiDevice> => {
    
    if (showDisconnectedDevices) {
      return raspis;
    }
    
    const filtered = Object.fromEntries(
      Object.entries(raspis).filter(([_, raspi]) => (raspi as RaspiDevice).raspi_status !== 'DISCONNECTED')
    ) as Record<string, RaspiDevice>;
    
    return filtered;
  };

  const toggleDisconnectedDevices = () => {
    const newValue = !showDisconnectedDevices;
    updatePreference('showDisconnectedDevices', newValue);
  };

  // Raspi menu operations - handlers match RaspiMenu component interface
  const handleRebootRaspi = (raspiId: string) => {
    if (raspiId !== selectedRaspiId) return;
    setShowRebootWarning(true);
  };

  const handleRestartRaspiSoftware = async (raspiId: string) => {
    if (raspiId !== selectedRaspiId) return;
    const runningExperiment = getRunningExperiment(raspiId);
    if (runningExperiment) {
      setShowRestartWarning(true);
      return;
    }
    try {
      await restartRaspiSoftware(raspiId);
    } catch (error) {
      console.error('Error restarting RASPI software:', error);
    }
  };

  const handleLoadRaspiData = (raspiId: string) => {
    if (raspiId !== selectedRaspiId) return;
    setLoadRaspiDataDialogOpen(true);
  };

  const handleCleanRaspiFiles = (raspiId: string) => {
    if (raspiId !== selectedRaspiId) return;
    setCleanRaspiFilesDialogOpen(true);
  };

  const handleUpdateRaspiSoftware = (raspiId: string) => {
    if (raspiId !== selectedRaspiId) return;
    setUpdateRaspiSoftwareDialogOpen(true);
  };

  // Experiment dialog handlers - separate logic for new vs edit
  const handleNewExperiment = () => {
    if (!selectedRaspiId) return;
    
    // Open dialog with "__NEW__" placeholder (backend will generate exp_id on save)
    setExperimentDialogExpId("__NEW__");
    setExperimentDialogOpen(true);
  };

  const handleEditExperiment = () => {
    // Get the latest experiment for this RASPI from the store
    const raspiExperiments = Object.values(allExperiments).filter(
      (exp: Experiment) => exp.raspi_id === selectedRaspiId
    );
    
    let currentExpId: string | null = null;
    
    if (raspiExperiments.length > 0) {
      // Sort by exp_id to get the latest (highest number)
      const sortedExperiments = raspiExperiments.sort((a: Experiment, b: Experiment) => {
        const aNum = parseInt(a.exp_id.replace('E', '')) || 0;
        const bNum = parseInt(b.exp_id.replace('E', '')) || 0;
        return bNum - aNum; // Descending order (latest first)
      });
      
      currentExpId = sortedExperiments[0].exp_id;
    }
    
    // Pass the current exp_id (if any) and the selected RASPI ID
    setExperimentDialogExpId(currentExpId);
    setExperimentDialogOpen(true);
  };

  const handleCloseExperimentDialog = () => {
    setExperimentDialogOpen(false);
    setExperimentDialogExpId(null);
  };

  // Helper function to check if there's a running experiment for a RASPI
  const getRunningExperiment = (raspiId: string): Experiment | null => {
    const experiments = useExperimentStore.getState().allExperiments;
    for (const experiment of Object.values(experiments)) {
      if (experiment.raspi_id === raspiId && experiment.exp_status === 'RUN') {
        return experiment;
      }
    }
    return null;
  };

  // Helper function to check if a peripheral is used in running experiment
  const isPeripheralUsedInRunningExperiment = (raspiId: string, peripheralType: 'chamber' | 'cameras'): boolean => {
    const runningExperiment = getRunningExperiment(raspiId);
    if (!runningExperiment) return false;
    
    if (peripheralType === 'chamber') {
      return runningExperiment.chamber_enabled;
    } else {
      return runningExperiment.cameras_enabled.some(camera => camera.enabled);
    }
  };

  // Show refresh confirmation dialog
  const showRefreshConfirmation = (type: 'chamber' | 'cameras', raspiId: string) => {
    setRefreshDialogType(type);
    setRefreshDialogRaspiId(raspiId);
    setRefreshDialogOpen(true);
  };

  // Handle refresh confirmation
  const handleRefreshConfirm = async () => {
    setRefreshDialogOpen(false);
    
    try {
      if (refreshDialogType === 'chamber') {
        await handleRefreshChamberInternal();
      } else {
        await handleRefreshCamerasInternal();
      }
    } catch (error) {
      console.error(`Error refreshing ${refreshDialogType}:`, error);
    }
  };

  // Handle refresh cancellation
  const handleRefreshCancel = () => {
    setRefreshDialogOpen(false);
  };

  // Internal refresh handlers (without confirmation)
  const handleRefreshChamberInternal = async () => {
    if (!refreshDialogRaspiId) return;
    
    try {
      const success = await refreshChamber(refreshDialogRaspiId);
      if (success) {
        // Refresh chamber data
        await fetchAllChambers();
      }
    } catch (error) {
      console.error('Error refreshing chamber:', error);
    }
  };

  const handleRefreshCamerasInternal = async () => {
    if (!refreshDialogRaspiId) return;
    
    try {
      const success = await refreshCameras(refreshDialogRaspiId);
      if (success) {
        // Refresh camera data
        await fetchAllCameras();
      }
    } catch (error) {
      console.error('Error refreshing cameras:', error);
    }
  };

  // Refresh cameras handler (with confirmation)
  const handleRefreshCameras = () => {
    if (!selectedRaspiId) return;
    showRefreshConfirmation('cameras', selectedRaspiId);
  };

  // Refresh chamber handler (with confirmation)
  const handleRefreshChamber = () => {
    if (!selectedRaspiId) return;
    showRefreshConfirmation('chamber', selectedRaspiId);
  };

  // Fetch data on component mount and when selected RASPI changes
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch all data in parallel
        await Promise.all([
          fetchAllRaspisRef.current(),
          fetchAllChambersRef.current(),
          fetchAllCamerasRef.current(),
          fetchAllExperimentsRef.current()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  // Fetch camera data specifically when selected RASPI changes
  useEffect(() => {
    if (selectedRaspiId) {
      fetchAllCamerasRef.current();
    }
  }, [selectedRaspiId]);

  // Initialize logs for selected raspi
  useEffect(() => {
    if (!selectedRaspiId) return;
    
    // Set log filter to raspi logs for selected device
    setSelectedRaspiId(selectedRaspiId);
    
    // Set current RASPI context for WebSocket log filtering
    websocketService.setCurrentRaspiContext(selectedRaspiId);
    
    // Fetch initial RASPI logs
    const fetchRaspiLogs = async () => {
      try {
        // Clear existing RASPI logs first
        clearLogs('raspi');
        
        // Fetch logs for the selected RASPI
        const response = await getRaspiLogs(selectedRaspiId, 100);
        if (response && response.logs) {
          // Parse logs into the unified format
          const parsedLogs = response.logs.map((log: any) => {
            // If we have a raw_line, try to parse the unified format
            if (log.raw_line && typeof log.raw_line === 'string') {
              const match = log.raw_line.match(/\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[(.+)\]/);
              if (match) {
                return {
                  timestamp: match[1],
                  origin: match[2],
                  level: match[3],
                  source_component: match[4],
                  message: match[5],
                  raw_line: log.raw_line
                };
              }
            }
            
            // Fallback to constructing from object properties
            return {
              timestamp: log.timestamp || new Date().toISOString(),
              origin: selectedRaspiId, // RASPI logs have RASPI ID as origin
              level: log.level || 'INFO',
              source_component: log.component || 'RASPI',
              message: log.message || 'Unknown log entry',
              raw_line: log.raw_line || `[${log.timestamp}] [${selectedRaspiId}] [${log.level}] [${log.component}] [${log.message}]`
            };
          });
          
          // Add the parsed logs to the store
          addLogEntries(parsedLogs, 'raspi');
        }
      } catch (error) {
        console.error('Error fetching RASPI logs:', error);
      }
    };
    
    fetchRaspiLogs();
  }, [selectedRaspiId, setSelectedRaspiId, clearLogs, addLogEntries]);

  const selectedRaspi = useMemo(() => getSelectedRaspi(), [selectedRaspiId, raspis]);
  const filteredRaspis = useMemo(() => getFilteredRaspis(), [raspis, showDisconnectedDevices]);

  // Set first available raspi as selected if none selected or if current selection is not in filtered list
  useEffect(() => {
    if (Object.keys(filteredRaspis).length > 0) {
      // If no RASPI is selected, or if the selected RASPI is not in the filtered list
      if (!selectedRaspiId || !filteredRaspis[selectedRaspiId]) {
        const firstRaspiId = Object.keys(filteredRaspis)[0];
        selectRaspi(firstRaspiId);
      }
    }
  }, [filteredRaspis, selectedRaspiId, selectRaspi]);

  const location = useLocation();
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raspiId = params.get('raspi_id');
    if (raspiId) {
      selectRaspi(raspiId);
    }
  }, [location.search, selectRaspi]);

  // Helper function to get status dot color
  const getStatusDotColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return '#4caf50';
      case 'IN_USE':
        return '#ff9800';
      case 'DISCONNECTED':
        return '#9e9e9e';
      case 'UNKNOWN':
        return '#757575';
      default:
        return '#ff9800';
    }
  };

  // Helper function to get log level color
  const getLogLevelColor = (level: string | undefined) => {
    switch ((level || '').toUpperCase()) {
      case 'ERROR':
        return '#f44336';
      case 'WARNING':
      case 'WARN':
        return '#ff9800';
      case 'INFO':
        return '#2196f3';
      case 'DEBUG':
        return '#9e9e9e';
      default:
        return '#000000';
    }
  };

  // Helper function to get component color
  const getComponentColor = (component: string) => {
    switch (component?.toUpperCase()) {
      case 'CHAMBER':
        return '#4caf50';
      case 'CAMERA':
      case 'CAMERAS':
        return '#2196f3';
      case 'EXP':
      case 'EXPERIMENT':
        return '#9c27b0';
      case 'RASPI':
        return '#ff9800';
      default:
        return '#757575';
    }
  };

  // Component filter state
  const [componentFilters, setComponentFilters] = useState({
    CHAMBER: true,
    CAMERA: true,
    EXP: true,
    RASPI: true,
  });

  // Toggle component filter
  const toggleComponentFilter = (component: string) => {
    setComponentFilters((prev: typeof componentFilters) => ({
      ...prev,
      [component]: !prev[component as keyof typeof prev]
    }));
  };

  // Simple approach: just get the raw logs and filter locally
  const raspiLogs = useLogsStore((state) => state.raspiLogs);
  
  const filteredLogEntries = useMemo(() => {
    // Filter by component first
    const componentFiltered = raspiLogs.filter((entry: LogEntry) => {
      const component = entry.source_component?.toUpperCase() || 'RASPI';
      return componentFilters[component as keyof typeof componentFilters] ?? true;
    });
    
    // Sort by timestamp (newest first)
    return componentFiltered.sort((a, b) => {
      const aTime = new Date(a.timestamp).getTime();
      const bTime = new Date(b.timestamp).getTime();
      return bTime - aTime; // Descending order (newest first)
    });
  }, [raspiLogs, componentFilters]);

  // Toggle log frame expansion (for arrow button inside log frame)
  const toggleLogFrameExpansion = () => {
    updatePreference('logFrameExpanded', !isLogFrameExpanded);
  };

  // Toggle log frame visibility (for hide/show button in top bar) - now using store
  const toggleLogFrameVisibility = () => {
    updatePreference('showRaspiLog', !showLogFrame);
  };

  // Log frame content with expand/collapse arrow - use useMemo to make it reactive
  const logFrameContent = useMemo(() => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        <IconButton 
          onClick={toggleLogFrameExpansion}
          size="small"
          sx={{ p: 0.5 }}
        >
          {isLogFrameExpanded ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <Typography variant="h6" gutterBottom>
          {selectedRaspiId ? `${selectedRaspiId} LOGS` : 'DEVICE LOGS'}
        </Typography>
        <Box 
          sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: selectedRaspi?.raspi_status === 'DISCONNECTED' ? '#9e9e9e' : '#4caf50',
            ml: 1
          }} 
        />
        <Typography variant="caption" color={selectedRaspi?.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'text.secondary'}>
          {selectedRaspi?.raspi_status === 'DISCONNECTED' ? 'Offline' : 'Live'}
        </Typography>
        
        {/* Spacer to push filters to the right */}
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Component Filters */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {Object.entries(componentFilters).map(([component, isEnabled]) => (
            <FormControlLabel
              key={component}
              control={
                <Checkbox
                  checked={isEnabled}
                  onChange={() => toggleComponentFilter(component)}
                  size="small"
                  sx={{
                    color: getComponentColor(component),
                    '&.Mui-checked': {
                      color: getComponentColor(component),
                    },
                    '& .MuiSvgIcon-root': {
                      typography: 'alertLabel',
                    },
                    p: 0,
                    m: 0
                  }}
                />
              }
              label={
                <Typography 
                  variant="caption" 
                  sx={{ 
                    typography: 'fieldLabel',
                    color: getComponentColor(component),
                    fontWeight: 'bold',
                    lineHeight: 1
                  }}
                >
                  {component}
                </Typography>
              }
              sx={{ 
                margin: 0,
                padding: 0,
                minHeight: 'auto',
                '& .MuiFormControlLabel-label': {
                  marginLeft: 0.3,
                  marginTop: 0,
                  marginBottom: 0
                }
              }}
            />
          ))}
        </Box>
      </Box>
      
      {/* Log Content - always show full logs */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        height: 0, // This ensures the box takes remaining space
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#fafafa'
      }}>
        {logLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : logEntries.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {filteredLogEntries.map((entry: LogEntry, index: number) => (
              <Box 
                key={index} 
                sx={{ 
                  p: 1, 
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography 
                    variant="logSection" 
                    sx={{ 
                      color: getLogLevelColor(entry.level),
                      minWidth: '60px',
                      flexShrink: 0
                    }}
                  >
                    {entry.level}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      typography: 'logText',
                      minWidth: '80px',
                      flexShrink: 0
                    }}
                  >
                    {entry.timestamp}
                  </Typography>
                  {entry.source_component && (
                    <Typography 
                      variant="logSection" 
                      sx={{ 
                        color: getComponentColor(entry.source_component),
                        flexShrink: 0,
                        backgroundColor: `${getComponentColor(entry.source_component)}15`,
                        px: 1,
                        py: 0.2,
                        borderRadius: '4px',
                        border: `1px solid ${getComponentColor(entry.source_component)}30`
                      }}
                    >
                      {entry.source_component}
                    </Typography>
                  )}
                </Box>
                <Typography 
                  variant="logText"
                  sx={{ 
                    wordBreak: 'break-word',
                    ml: entry.source_component ? 16 : 14,
                    mt: 0.5,
                    color: '#333'
                  }}
                >
                  {entry.message}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No log entries found
          </Typography>
        )}
      </Box>
    </Box>
  ), [isLogFrameExpanded, logLoading, logEntries, selectedRaspiId, toggleLogFrameExpansion, filteredLogEntries, componentFilters]);

  // Up frame content with Raspi ID dropdown and status indicators
  const upFrameContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexGrow: 1, justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {/* Filter Toggle - Reduced size with text below */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showDisconnectedDevices}
                onChange={toggleDisconnectedDevices}
                size="small"
              />
            }
            label=""
            sx={{ 
              margin: 0,
              '& .MuiFormControlLabel-label': {
                display: 'none'
              }
            }}
          />
          <Typography variant="fieldLabel" sx={{ textAlign: 'center' }}>
            SHOW<br />DISCONNECTED
          </Typography>
        </Box>

        {/* Raspi ID Dropdown - Keep width, larger font */}
        <FormControl sx={{ minWidth: 140, maxWidth: 160, mt: 0.5 }}>
          <Select
            value={selectedRaspiId || ''}
            onChange={(e: any) => selectRaspi(e.target.value)}
            displayEmpty
            disabled={Object.keys(filteredRaspis).length === 0}
            sx={{
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              '& .MuiSelect-select': {
                fontSize: theme.typography.sectionHeader.fontSize,
                fontWeight: 'bold',
                color: 'primary.main'
              }
            }}
          >
            {Object.entries(filteredRaspis).map(([id, raspi]) => (
              <MenuItem 
                key={id} 
                value={id} 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity: raspi.raspi_status === 'DISCONNECTED' ? 0.7 : 1,
                  fontStyle: raspi.raspi_status === 'DISCONNECTED' ? 'italic' : 'normal'
                }}
              >
                {/* Status Dot */}
                <StatusDotIcon 
                  sx={{ 
                    color: getStatusDotColor(raspi.raspi_status),
                    typography: 'alertLabel'
                  }} 
                />
                {/* Device ID */}
                <Typography variant="cardName" sx={{ color: 'text.primary' }}>
                  {id}
                </Typography>
              </MenuItem>
            ))}
            {Object.keys(filteredRaspis).length === 0 && (
              <MenuItem value="" disabled>
                {showDisconnectedDevices ? 'No devices found' : 'No connected devices'}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Status Indicators - Custom icon-based indicators */}
        <RaspiStatusIndicator
          status={selectedRaspi?.raspi_status || 'UNKNOWN'}
          statusDescription={undefined}
          health={selectedRaspi?.raspi_health_status || 'UNKNOWN'}
          healthDescription={selectedRaspi?.raspi_health_message}
          alert={selectedRaspi?.raspi_alert || 'UNKNOWN'}
          alertDescription={selectedRaspi?.raspi_alert_description}
        />

        {/* Memory Progress Bars */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 100 }}>
          {/* RAM Progress Bar */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ 
                width: 60,
                height: 8, 
                backgroundColor: '#bdbdbd', 
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative'
              }}>
                <Box sx={{ 
                  height: '100%', 
                  backgroundColor: selectedRaspi?.raspi_free_ram_gb && selectedRaspi?.raspi_total_ram_gb 
                    ? (() => {
                        const ratio = selectedRaspi.raspi_free_ram_gb / selectedRaspi.raspi_total_ram_gb;
                        if (ratio > 0.5) return '#4caf50'; // Green - plenty of RAM
                        if (ratio > 0.2) return '#ff9800'; // Orange - moderate RAM
                        return '#f44336'; // Red - low RAM
                      })()
                    : '#9e9e9e',
                  width: selectedRaspi?.raspi_free_ram_gb && selectedRaspi?.raspi_total_ram_gb 
                    ? `${(selectedRaspi.raspi_free_ram_gb / selectedRaspi.raspi_total_ram_gb) * 100}%`
                    : '0%',
                  transition: 'width 0.3s ease'
                }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
                <Typography variant="alertLabel" sx={{ 
                  fontWeight: 'bold', 
                  textAlign: 'center',
                  color: selectedRaspi?.raspi_free_ram_gb && selectedRaspi?.raspi_total_ram_gb ? '#333' : '#9e9e9e'
                }}>
                  {selectedRaspi?.raspi_free_ram_gb && selectedRaspi?.raspi_total_ram_gb 
                    ? `${selectedRaspi.raspi_free_ram_gb.toFixed(1)}/${selectedRaspi.raspi_total_ram_gb.toFixed(1)}GB`
                    : 'N/A'
                  }
                </Typography>
                <Typography variant="fieldLabel" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                  RAM
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* SD Card Progress Bar */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ 
                width: 60,
                height: 8, 
                backgroundColor: '#bdbdbd', 
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative'
              }}>
                <Box sx={{ 
                  height: '100%', 
                  backgroundColor: selectedRaspi?.raspi_free_sd_gb && selectedRaspi?.raspi_total_sd_gb 
                    ? (() => {
                        const ratio = selectedRaspi.raspi_free_sd_gb / selectedRaspi.raspi_total_sd_gb;
                        if (ratio > 0.3) return '#4caf50'; // Green - plenty of SD space
                        if (ratio > 0.1) return '#ff9800'; // Orange - moderate SD space
                        return '#f44336'; // Red - low SD space
                      })()
                    : '#9e9e9e',
                  width: selectedRaspi?.raspi_free_sd_gb && selectedRaspi?.raspi_total_sd_gb 
                    ? `${(selectedRaspi.raspi_free_sd_gb / selectedRaspi.raspi_total_sd_gb) * 100}%`
                    : '0%',
                  transition: 'width 0.3s ease'
                }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
                <Typography variant="alertLabel" sx={{ 
                  fontWeight: 'bold', 
                  textAlign: 'center',
                  color: selectedRaspi?.raspi_free_sd_gb && selectedRaspi?.raspi_total_sd_gb ? '#333' : '#9e9e9e'
                }}>
                  {selectedRaspi?.raspi_free_sd_gb && selectedRaspi?.raspi_total_sd_gb 
                    ? `${selectedRaspi.raspi_free_sd_gb.toFixed(1)}/${selectedRaspi.raspi_total_sd_gb.toFixed(1)}GB`
                    : 'N/A'
                  }
                </Typography>
                <Typography variant="fieldLabel" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                  SD
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Raspi Menu */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          {selectedRaspiId && (
            <RaspiMenu
              raspiId={selectedRaspiId}
              raspiStatus={selectedRaspi?.raspi_status || 'DISCONNECTED'}
              isViewer={isViewer}
              triggerUnauthorized={triggerUnauthorized}
              onLoadRaspiData={handleLoadRaspiData}
              onCleanRaspiFiles={handleCleanRaspiFiles}
              onRebootRaspi={handleRebootRaspi}
              onRestartRaspiSoftware={handleRestartRaspiSoftware}
              onUpdateRaspiSoftware={handleUpdateRaspiSoftware}
            />
          )}
          <Typography variant="fieldLabel" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            MENU
          </Typography>
        </Box>
      </Box>

      {/* Right side - Show/Hide Log */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={toggleLogFrameVisibility} color="primary">
          {showLogFrame ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </Box>
    </Box>
  );

  // Ensure only one declaration of centralFrameContent exists, before the return statement.
  // Remove any duplicate or misplaced code after its definition.
  const centralFrameContent = (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!selectedRaspi ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          gap: 2,
          color: 'text.secondary'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            No Device Selected
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            {Object.keys(filteredRaspis).length === 0 
              ? 'No devices are currently available. Please check your connection to the hub.'
              : 'Please select a device from the dropdown above to view its details.'
            }
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.disabled' }}>
            Debug: selectedRaspiId={selectedRaspiId}, filteredRaspis={Object.keys(filteredRaspis).join(', ')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          opacity: selectedRaspi.raspi_status === 'DISCONNECTED' ? 0.6 : 1,
          pointerEvents: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'none' : 'auto',
          position: 'relative'
        }}>
          {/* Disconnected Overlay */}
          {selectedRaspi.raspi_status === 'DISCONNECTED' && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(128, 128, 128, 0.1)',
              zIndex: 1,
              pointerEvents: 'none',
              borderRadius: 1
            }} />
          )}
          {/* CHAMBER Section */}
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 2,
            backgroundColor: '#fafafa',
            position: 'relative',
            zIndex: 2
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.secondary' : 'primary.main'
              }}>
                CHAMBER
              </Typography>
              <IconButton 
                size="small" 
                disabled={selectedRaspi.raspi_status === 'DISCONNECTED'}
                sx={{ 
                  color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'primary.main',
                  '&:hover': { 
                    backgroundColor: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'transparent' : 'primary.light', 
                    color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'white' 
                  }
                }}
                onClick={isViewer ? () => triggerUnauthorized() : handleRefreshChamber}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
            {!selectedRaspi?.chamber_id ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 3,
                color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'text.secondary'
              }}>
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  No chamber connected to this device
                </Typography>
              </Box>
            ) : (
              <ChamberCard 
                selectedRaspi={selectedRaspi}
                selectedRaspiId={selectedRaspiId}
                onDataRefresh={fetchAllRaspis}
              />
            )}
          </Box>
          {/* CAMERAS Section */}
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 2,
            backgroundColor: '#fafafa',
            position: 'relative',
            zIndex: 2
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.secondary' : 'primary.main'
              }}>
                CAMERAS
              </Typography>
              <IconButton 
                size="small" 
                disabled={selectedRaspi.raspi_status === 'DISCONNECTED'}
                sx={{ 
                  color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'primary.main',
                  '&:hover': { 
                    backgroundColor: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'transparent' : 'primary.light', 
                    color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'white' 
                  }
                }}
                onClick={isViewer ? () => triggerUnauthorized() : handleRefreshCameras}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
            {!selectedRaspi?.camera_ids || selectedRaspi.camera_ids.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 3,
                color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'text.secondary'
              }}>
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  No cameras connected to this device
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedRaspi.camera_ids.map((cameraId) => {
                  const cameraData = allCameras[cameraId];
                  if (!cameraData) {
                    return null; // Camera data not available yet
                  }
                  return (
                    <CameraCard
                      key={cameraId}
                      cameraId={cameraId}
                      cameraData={cameraData}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
          {/* EXPERIMENT Section */}
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 2,
            backgroundColor: '#fafafa',
            position: 'relative',
            zIndex: 2
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.secondary' : 'primary.main'
              }}>
                EXPERIMENT
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="small" 
                  disabled={selectedRaspi.raspi_status === 'DISCONNECTED'}
                  sx={{ 
                    color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'primary.main',
                    '&:hover': { 
                      backgroundColor: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'transparent' : 'primary.light', 
                      color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'white' 
                    }
                  }}
                  onClick={isViewer ? () => triggerUnauthorized() : handleEditExperiment}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  disabled={selectedRaspi.raspi_status === 'DISCONNECTED'}
                  sx={{ 
                    color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'primary.main',
                    '&:hover': { 
                      backgroundColor: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'transparent' : 'primary.light', 
                      color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'white' 
                    }
                  }}
                  onClick={isViewer ? () => triggerUnauthorized() : handleNewExperiment}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  disabled={selectedRaspi.raspi_status === 'DISCONNECTED'}
                  sx={{ 
                    color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'primary.main',
                    '&:hover': { 
                      backgroundColor: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'transparent' : 'primary.light', 
                      color: selectedRaspi.raspi_status === 'DISCONNECTED' ? 'text.disabled' : 'white' 
                    }
                  }}
                  onClick={isViewer ? () => triggerUnauthorized() : () => fetchAllExperiments()}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <ExperimentCard 
              selectedRaspiId={selectedRaspiId}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  // Add handlers for the warning dialog
  const handleConfirmReboot = async () => {
    setShowRebootWarning(false);
    if (!selectedRaspiId) return;
    try {
      await rebootRaspi(selectedRaspiId);
    } catch (error) {
      console.error('Error rebooting RASPI:', error);
    }
  };
  const handleCancelReboot = () => {
    setShowRebootWarning(false);
  };

  // Add handlers for the warning dialog
  const handleConfirmRestart = async () => {
    setShowRestartWarning(false);
    if (!selectedRaspiId) return;
    try {
      await restartRaspiSoftware(selectedRaspiId);
    } catch (error) {
      console.error('Error restarting RASPI software:', error);
    }
  };
  const handleCancelRestart = () => {
    setShowRestartWarning(false);
  };

  return (
    <>
      <PageLayout
        upFrameContent={upFrameContent}
        centralFrameContent={centralFrameContent}
        logFrameContent={logFrameContent}
        showLogFrame={showLogFrame}
        isLogFrameExpanded={isLogFrameExpanded}
      />


      {/* Experiment Dialog */}
      <ExperimentEditDialog
        open={experimentDialogOpen}
        onClose={handleCloseExperimentDialog}
        initialExpId={experimentDialogExpId}
        initiatingRaspiId={selectedRaspiId}
      />

      {/* Refresh Confirmation Dialog */}
      <RefreshConfirmationDialog
        open={refreshDialogOpen}
        onClose={handleRefreshCancel}
        onConfirm={handleRefreshConfirm}
        peripheralType={refreshDialogType}
        raspiId={refreshDialogRaspiId}
        hasRunningExperiment={isPeripheralUsedInRunningExperiment(refreshDialogRaspiId, refreshDialogType)}
        runningExperimentInfo={getRunningExperiment(refreshDialogRaspiId) ? {
          exp_id: getRunningExperiment(refreshDialogRaspiId)!.exp_id,
          test_id: getRunningExperiment(refreshDialogRaspiId)!.test_id,
          profile_id: getRunningExperiment(refreshDialogRaspiId)!.profile_id
        } : undefined}
      />

      {/* Load RASPI Data Dialog */}
      <LoadRaspiDataDialog
        open={loadRaspiDataDialogOpen}
        onClose={() => setLoadRaspiDataDialogOpen(false)}
        raspiId={selectedRaspiId || ''}
      />

      {/* Clean RASPI Files Dialog */}
      <CleanRaspiFilesDialog
        open={cleanRaspiFilesDialogOpen}
        onClose={() => setCleanRaspiFilesDialogOpen(false)}
        raspiId={selectedRaspiId || ''}
      />

      {/* Update RASPI Software Dialog */}
      <UpdateRaspiSoftwareDialog
        open={updateRaspiSoftwareDialogOpen}
        onClose={() => setUpdateRaspiSoftwareDialogOpen(false)}
        raspiId={selectedRaspiId || ''}
        currentRaspiVersion={getSelectedRaspi()?.raspi_software_version || '1.0.0'}
      />

      {/* Reboot Warning Dialog */}
      {showRebootWarning && (
        <Dialog open={showRebootWarning} onClose={handleCancelReboot}>
          <DialogTitle>Warning: Reboot RASPI</DialogTitle>
          <DialogContent>
            {getRunningExperiment(selectedRaspiId || '') ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  An experiment is currently running on this RASPI.
                </Typography>
                <Typography variant="body2">
                  <strong>Rebooting will abruptly stop the experiment and all unsaved data will be lost.</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Are you sure you want to continue?
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Rebooting the RASPI is a dangerous operation.
                </Typography>
                <Typography variant="body2">
                  This will interrupt all processes and may cause data loss or system instability.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Are you sure you want to continue?
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelReboot} color="inherit">Cancel</Button>
            <Button onClick={handleConfirmReboot} color="error" variant="contained">Reboot Anyway</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Restart Warning Dialog */}
      {showRestartWarning && (
        <Dialog open={showRestartWarning} onClose={handleCancelRestart}>
          <DialogTitle>Warning: Experiment Running</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                An experiment is currently running on this RASPI.
              </Typography>
              <Typography variant="body2">
                Restarting the software will abruptly end the experiment and all unsaved data will be lost. Are you sure you want to continue?
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelRestart} color="inherit">Cancel</Button>
            <Button onClick={handleConfirmRestart} color="error" variant="contained">Restart Anyway</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default RaspiDetails;
